import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

interface SubscriptionStatusMonitorProps {
  enabled?: boolean
}

const SubscriptionStatusMonitor = ({ enabled = false }: SubscriptionStatusMonitorProps) => {
  const { user, session } = useAuth()
  const [statusHistory, setStatusHistory] = useState<Array<{
    timestamp: string
    status: string
    source: string
    cached: boolean
  }>>([])
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (!enabled || !user) return

    // Log current status
    const logStatus = (source: string, cached = false) => {
      const entry = {
        timestamp: new Date().toISOString(),
        status: user.subscriptionStatus || 'null',
        source,
        cached
      }
      
      setStatusHistory(prev => [...prev.slice(-9), entry]) // Keep last 10 entries
      
      // Log to console for debugging
      console.log('🔍 Subscription Status Monitor:', entry)
    }

    logStatus('component-mount')

    // Monitor for status changes
    const interval = setInterval(() => {
      if (user) {
        logStatus('periodic-check')
      }
    }, 30000) // Check every 30 seconds

    return () => clearInterval(interval)
  }, [user, enabled])

  // Check database status directly
  const checkDatabaseStatus = async () => {
    if (!user || !supabase) return

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('subscription_status, subscription_id, email')
        .eq('id', user.id)
        .single()

      if (error) {
        console.error('Database check error:', error)
        return
      }

      console.log('🔍 Direct database check:', {
        email: data.email,
        db_status: data.subscription_status,
        current_status: user.subscriptionStatus,
        match: data.subscription_status === user.subscriptionStatus
      })

      const entry = {
        timestamp: new Date().toISOString(),
        status: data.subscription_status || 'null',
        source: 'database-direct',
        cached: false
      }
      
      setStatusHistory(prev => [...prev.slice(-9), entry])
    } catch (error) {
      console.error('Database status check failed:', error)
    }
  }

  // Check cached status
  const checkCachedStatus = () => {
    if (!user) return

    try {
      const cached = localStorage.getItem(`shootsuite_user_${user.id}`)
      const backup = localStorage.getItem(`shootsuite_user_backup_${user.id}`)
      
      console.log('🔍 Cache status check:', {
        has_cache: !!cached,
        has_backup: !!backup,
        current_status: user.subscriptionStatus
      })

      if (cached) {
        const data = JSON.parse(cached)
        const entry = {
          timestamp: new Date().toISOString(),
          status: data.user?.subscriptionStatus || 'null',
          source: 'cache-regular',
          cached: true
        }
        setStatusHistory(prev => [...prev.slice(-9), entry])
      }

      if (backup) {
        const data = JSON.parse(backup)
        const entry = {
          timestamp: new Date().toISOString(),
          status: data.user?.subscriptionStatus || 'null',
          source: 'cache-backup',
          cached: true
        }
        setStatusHistory(prev => [...prev.slice(-9), entry])
      }
    } catch (error) {
      console.error('Cache check failed:', error)
    }
  }

  if (!enabled || !user) return null

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        onClick={() => setIsVisible(!isVisible)}
        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg"
      >
        🔍 Sub Monitor
      </button>
      
      {isVisible && (
        <div className="absolute bottom-12 right-0 w-96 bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-4 max-h-96 overflow-y-auto">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-white font-semibold">Subscription Monitor</h3>
            <button
              onClick={() => setIsVisible(false)}
              className="text-gray-400 hover:text-white"
            >
              ×
            </button>
          </div>
          
          <div className="space-y-2 mb-4">
            <div className="text-sm">
              <span className="text-gray-400">Current Status:</span>
              <span className={`ml-2 px-2 py-1 rounded text-xs ${
                user.subscriptionStatus === 'professional' ? 'bg-blue-600 text-white' :
                user.subscriptionStatus === 'studio' ? 'bg-purple-600 text-white' :
                'bg-gray-600 text-white'
              }`}>
                {user.subscriptionStatus || 'null'}
              </span>
            </div>
            <div className="text-sm">
              <span className="text-gray-400">User ID:</span>
              <span className="text-white ml-2 font-mono text-xs">{user.id.slice(0, 8)}...</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-400">Session:</span>
              <span className="text-white ml-2">{session ? '✅' : '❌'}</span>
            </div>
          </div>
          
          <div className="flex gap-2 mb-4">
            <button
              onClick={checkDatabaseStatus}
              className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
            >
              Check DB
            </button>
            <button
              onClick={checkCachedStatus}
              className="bg-orange-600 hover:bg-orange-700 text-white px-2 py-1 rounded text-xs"
            >
              Check Cache
            </button>
          </div>
          
          <div className="space-y-1">
            <h4 className="text-gray-400 text-xs font-semibold">Status History:</h4>
            {statusHistory.slice(-5).reverse().map((entry, index) => (
              <div key={index} className="text-xs bg-gray-800 rounded p-2">
                <div className="flex justify-between">
                  <span className={`px-1 rounded ${
                    entry.status === 'professional' ? 'bg-blue-600 text-white' :
                    entry.status === 'studio' ? 'bg-purple-600 text-white' :
                    'bg-gray-600 text-white'
                  }`}>
                    {entry.status}
                  </span>
                  <span className="text-gray-400">{entry.source}</span>
                </div>
                <div className="text-gray-500 mt-1">
                  {new Date(entry.timestamp).toLocaleTimeString()}
                  {entry.cached && ' (cached)'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default SubscriptionStatusMonitor 